import React, {useCallback, useEffect, useRef, useState} from "react";
import {Link} from "react-router-dom";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";
import Toast from "../components/Toast";
import {
  useResourcePersonAuth,
  useResourcePersonDashboard,
} from "../store/resourcePerson/resourcePersonStore";
import {Avatar, AvatarFallback, AvatarImage} from "../components/ui/avatar";
import {Card, CardContent, CardHeader, CardTitle} from "../components/ui/card";
import {Progress} from "../components/ui/progress";
import {
  Activity,
  ArrowUpRight,
  Award,
  BookOpen,
  BookOpenText,
  CircleDollarSign,
  Clock3,
  Coins,
  FileText,
  GraduationCap,
  LayoutDashboard,
  Library,
  Mail,
  Phone,
  ShoppingBag,
  Sparkles,
  Users,
  Wallet,
} from "lucide-react";
import {imageUrl} from "../lib/format";

const numberFormat = new Intl.NumberFormat("en-NG");
const currencyFormat = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  maximumFractionDigits: 0,
});

const numberValue = (input) => {
  const parsed = Number(input);
  return Number.isFinite(parsed) ? parsed : 0;
};
const money = (amount) => currencyFormat.format(numberValue(amount));
const personName = (person) => person?.name || person?.first_name || "Resource Person";
const resourceTitle = (item) =>
  item?.title || item?.name || item?.courseTitle || item?.bookTitle || item?.journalTitle || "Untitled resource";
const formatDate = (date) => {
  if (!date) return "Recently";
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime())
    ? "Recently"
    : parsed.toLocaleDateString("en-NG", {day: "numeric", month: "short", year: "numeric"});
};
const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
// Transaction types returned by GET /api/resource-person/dashboard.
const SALE_TYPE_LABELS = {
  COURSE_PURCHASE: "Course purchase",
  BOOK_PURCHASE: "Book purchase",
  JOURNAL_PURCHASE: "Journal purchase",
  LEARNING_PATH_PURCHASE: "Learning path purchase",
  PROJECT_TOPIC_PURCHASE: "Project topic purchase",
  SEMINAR_TOPIC_PURCHASE: "Seminar topic purchase",
  RESEARCH_MATERIAL_PURCHASE: "Research material purchase",
  MANUAL_PURCHASE: "Manual purchase",
  CBT_SUBSCRIPTION: "CBT subscription",
  MANUAL_PAYMENT: "Manual payment",
  REFUND: "Refund",
  WITHDRAWAL: "Withdrawal",
  CART_PURCHASE: "Cart purchase",
};
const saleTypeLabel = (type) => SALE_TYPE_LABELS[type] || titleCase(type) || "Sale";

function EmptyState({icon: Icon = Activity, children}) {
  return (
    <div className="flex min-h-28 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/70 px-4 text-center text-sm text-slate-500">
      <Icon className="h-5 w-5 text-slate-400" />
      {children}
    </div>
  );
}

const ResourcePersonDashboard = () => {
  const {user, fetchMe} = useResourcePersonAuth();
  const {dashboard, dashboardLoading, dashboardError, fetchDashboard} =
    useResourcePersonDashboard();
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);
  const hasLoadedRef = useRef(false);

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    Promise.all([fetchDashboard(), fetchMe()]).then((results) => {
      const failed = results.find((result) => !result.success);
      if (failed) showToast(failed.error || "Failed to load dashboard", false);
    });
  }, [fetchDashboard, fetchMe, showToast]);

  if (dashboardLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <ResourcePersonNavBar />
        <div className="flex min-h-[70vh] items-center justify-center">
          <div className="loader" />
        </div>
      </div>
    );
  }

  const dashboardData = dashboard || {};
  const summary = dashboardData.summary || {};
  const earnings = dashboardData.earnings || {};
  const students = dashboardData.students || {};
  const analytics = dashboardData.analytics || {};
  const person = summary.name ? summary : user || {};
  const status = summary.status || user?.status || "ACTIVE";
  const isActive = status === "ACTIVE";
  const email = summary.email || user?.email || "";
  const phone = summary.phone || user?.phone || "";
  const bio = summary.bio || user?.bio || "";
  const newestStudents = Array.isArray(students.newest) ? students.newest : [];
  const recentSales = Array.isArray(dashboardData.recentSales) ? dashboardData.recentSales : [];
  const recentWithdrawals = Array.isArray(dashboardData.recentWithdrawals)
    ? dashboardData.recentWithdrawals
    : [];
  const activity = Array.isArray(dashboardData.activity) ? dashboardData.activity : [];

  const completionRate = Math.min(100, Math.max(0, numberValue(students.completionRate)));

  const stats = [
    ["Courses", summary.totalCourses, BookOpen, "text-blue-600", "/resource-person/content-library"],
    ["Books", summary.totalBooks, BookOpenText, "text-amber-600"],
    ["Journals", summary.totalJournals, FileText, "text-violet-600"],
    ["Students", summary.totalStudents, GraduationCap, "text-emerald-600"],
    ["Enrollments", summary.totalEnrollments, Users, "text-cyan-600"],
    ["Lifetime earnings", summary.lifetimeEarnings, Coins, "text-orange-600", null, true],
    ["Pending earnings", summary.pendingEarnings, Clock3, "text-amber-600", null, true],
    ["Available balance", summary.availableBalance, Wallet, "text-green-600", null, true],
    ["Reserved balance", summary.reservedBalance, Wallet, "text-violet-600", null, true],
    ["Total withdrawn", summary.totalWithdrawn, ArrowUpRight, "text-slate-600", null, true],
    ["Withdrawals requested", summary.totalWithdrawalsRequested, FileText, "text-cyan-600"],
    ["Successful withdrawals", summary.totalSuccessfulWithdrawals, Award, "text-emerald-600"],
  ];

  const topContent = [
    ["Best-selling course", analytics.bestSellingCourse, BookOpen, "revenue"],
    ["Best-selling book", analytics.bestSellingBook, Library, "revenue"],
    ["Best-selling journal", analytics.bestSellingJournal, FileText, "revenue"],
    ["Most enrolled course", analytics.mostEnrolledCourse, Users, "enrollmentCount"],
    ["Most downloaded book", analytics.mostDownloadedBook, Library, "downloads"],
    ["Most downloaded journal", analytics.mostDownloadedJournal, FileText, "downloads"],
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <ResourcePersonNavBar />
      {toast ? <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} /> : null}
      {dashboardError ? (
        <div className="mx-4 mt-20 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 sm:mx-6">
          {dashboardError}
        </div>
      ) : null}

      <main className="py-6 pt-2">
        <div className="mx-auto max-w-7xl space-y-6">
          <section className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div>
                <p className="mb-1 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-cyan-700">
                  <LayoutDashboard className="h-3.5 w-3.5" /> Resource person dashboard
                </p>
                <h1 className="text-2xl font-semibold text-slate-950 sm:text-3xl">
                  Welcome back, {personName(person)}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                  {email ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-slate-400" /> {email}
                    </span>
                  ) : null}
                  {phone ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5 text-slate-400" /> {phone}
                    </span>
                  ) : null}
                </div>
                {bio ? <p className="mt-2 max-w-2xl text-sm text-slate-500">{bio}</p> : null}
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-2 self-start rounded-full border px-3 py-1.5 text-xs font-semibold ${
                isActive
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-rose-200 bg-rose-50 text-rose-700"
              }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${isActive ? "bg-emerald-400" : "bg-rose-400"}`}
              />
              {status}
            </span>
          </section>

          <section className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-6">
            {stats.map(([label, amount, Icon, color, to, isMoney]) => {
              const card = (
                <Card className="h-full border-slate-200 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                  <CardContent className="flex h-full flex-col gap-1.5 p-2.5">
                    <div className={`flex h-7 w-7 items-center justify-center rounded-md bg-slate-100 ${color}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <p className="text-[11px] font-medium leading-tight text-slate-500">{label}</p>
                      <p className="text-base font-bold leading-tight text-slate-900">
                        {isMoney ? money(amount) : numberFormat.format(numberValue(amount))}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
              return to ? (
                <Link key={label} to={to}>
                  {card}
                </Link>
              ) : (
                <React.Fragment key={label}>{card}</React.Fragment>
              );
            })}
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Earnings overview</CardTitle>
                  <p className="mt-1 text-sm text-slate-500">Track your resource performance</p>
                </div>
                <CircleDollarSign className="h-6 w-6 text-emerald-600" />
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[
                  ["Today", earnings.today, true],
                  ["Last 7 days", earnings.last7Days, true],
                  ["Last 30 days", earnings.last30Days, true],
                  ["This month", earnings.currentMonth, true],
                  ["Previous month", earnings.previousMonth, true],
                  ["Growth", earnings.growth, false],
                ].map(([label, amount, isMoney]) => (
                  <div key={label} className="rounded-lg bg-slate-50 p-4">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">
                      {isMoney ? money(amount) : `${numberValue(amount)}%`}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Student progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{completionRate}%</p>
                    <p className="text-sm text-slate-500">Completion rate</p>
                  </div>
                  <Award className="h-8 w-8 text-amber-500" />
                </div>
                <Progress value={completionRate} className="h-2.5" />
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div>
                    <p className="font-bold text-slate-900">{numberFormat.format(numberValue(students.active))}</p>
                    <p className="text-slate-500">Active</p>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{numberFormat.format(numberValue(students.completed))}</p>
                    <p className="text-slate-500">Completed</p>
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{numberFormat.format(newestStudents.length)}</p>
                    <p className="text-slate-500">Newest</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Sparkles className="h-5 w-5 text-amber-500" /> Top content
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[300px] space-y-2 overflow-y-auto">
                {topContent.map(([label, item, Icon, metric]) => {
                  const cover = typeof item?.coverImage === "string" ? imageUrl(item.coverImage) : null;
                  return (
                    <div key={label} className="flex items-center justify-between rounded-lg border border-slate-100 p-3">
                      <div className="flex min-w-0 items-center gap-3">
                        {cover ? (
                          <img src={cover} alt="" className="h-10 w-10 rounded-md object-cover" />
                        ) : (
                          <div className="rounded-md bg-slate-100 p-2 text-slate-500">
                            <Icon className="h-4 w-4" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-xs text-slate-500">{label}</p>
                          <p className="truncate text-sm font-semibold text-slate-800">
                            {item ? resourceTitle(item) : "No data yet"}
                          </p>
                          {item ? (
                            <p className="mt-1 text-xs text-slate-500">
                              {metric === "revenue"
                                ? `Revenue ${money(item.revenue)} · Price ${money(item.price)}`
                                : metric === "downloads"
                                  ? `${numberFormat.format(numberValue(item.downloads))} downloads · Price ${money(item.price)}`
                                  : `${numberFormat.format(numberValue(item.enrollmentCount))} enrollments · Price ${money(item.price)}`}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      {item ? <ArrowUpRight className="h-4 w-4 text-slate-400" /> : null}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-lg">Newest students</CardTitle>
                <Users className="h-5 w-5 text-cyan-600" />
              </CardHeader>
              <CardContent className="max-h-[300px] overflow-y-auto">
                {newestStudents.length ? (
                  <div className="space-y-3">
                    {newestStudents.map((student, index) => (
                      <div
                        key={student.id || index}
                        className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={imageUrl(student.avatar)} />
                            <AvatarFallback>{(student.name || "S").charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-slate-800">
                              {student.name || "Learner"}
                            </p>
                            {student.email ? (
                              <p className="truncate text-xs text-slate-500">{student.email}</p>
                            ) : null}
                          </div>
                        </div>
                        <span className="shrink-0 text-xs text-slate-500">
                          {formatDate(student.enrolledAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={GraduationCap}>Students will appear here after enrollment.</EmptyState>
                )}
              </CardContent>
            </Card>
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.25fr_1fr]">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-lg">Recent sales</CardTitle>
                <ShoppingBag className="h-5 w-5 text-emerald-600" />
              </CardHeader>
              <CardContent className="max-h-[300px] overflow-y-auto">
                {recentSales.length ? (
                  <div className="space-y-3">
                    {recentSales.map((sale, index) => (
                      <div
                        key={sale.id || index}
                        className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0 last:pb-0"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">
                            {saleTypeLabel(sale.type)}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {[sale.learnerName || "Learner", formatDate(sale.purchasedAt)]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                        <span className="shrink-0 font-semibold text-emerald-600">
                          {money(sale.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={ShoppingBag}>Your first sale will appear here.</EmptyState>
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-lg">Recent activity</CardTitle>
                <Clock3 className="h-5 w-5 text-violet-600" />
              </CardHeader>
              <CardContent className="max-h-[300px] overflow-y-auto">
                {activity.length ? (
                  <div className="space-y-3">
                    {activity.map((entry, index) => (
                      <div key={entry.id || index} className="flex gap-3">
                        <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-violet-500" />
                        <div>
                          <p className="text-sm text-slate-700">{entry.action || "Activity recorded"}</p>
                          <p className="text-xs text-slate-500">
                            {formatDate(entry.createdAt)}
                            {entry.targetType ? ` · ${titleCase(entry.targetType)}` : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState>No recent activity.</EmptyState>
                )}
              </CardContent>
            </Card>
          </section>

          <section>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-lg">Recent withdrawals</CardTitle>
                <Wallet className="h-5 w-5 text-slate-600" />
              </CardHeader>
              <CardContent className="max-h-[300px] overflow-y-auto">
                {recentWithdrawals.length ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    {recentWithdrawals.map((withdrawal, index) => (
                      <div
                        key={withdrawal.id || index}
                        className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">
                            Withdrawal
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {[
                              titleCase(withdrawal.status),
                              `Requested ${formatDate(withdrawal.requestedAt)}`,
                              withdrawal.approvedAt
                                ? `Approved ${formatDate(withdrawal.approvedAt)}`
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        </div>
                        <span className="shrink-0 font-semibold text-slate-800">
                          {money(withdrawal.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={Wallet}>No withdrawal requests yet.</EmptyState>
                )}
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ResourcePersonDashboard;
